import { jsPDF } from "jspdf";
import {
  formatAmountPaise,
  formatMembershipDate,
  parseMembershipDate,
  PREMIUM_DURATION_LABEL,
} from "./membership";
import { paymentRecordToReceipt } from "./paymentReceipt";

const safe = (value) => (value == null || value === "" ? "—" : String(value));

function hexRgb(hex) {
  const value = hex.replace("#", "");
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

export function downloadPaymentReceipt(payment) {
  if (!payment) throw new Error("No payment data");

  const receipt = paymentRecordToReceipt(payment);
  const paidAt = formatMembershipDate(parseMembershipDate(receipt.paidAt));
  const validFrom = formatMembershipDate(parseMembershipDate(receipt.validFrom));
  const validUntil = formatMembershipDate(parseMembershipDate(receipt.validUntil));
  const amount =
    receipt.amount != null
      ? formatAmountPaise(receipt.amount, receipt.currency)
      : "—";
  const isFailed = String(receipt.status || "").toLowerCase() === "failed";
  const colors = {
    ink: "#231B1D",
    muted: "#756B6D",
    soft: "#9A8F91",
    line: "#EEE7E8",
    pale: "#FAF7F7",
    brand: "#C90243",
    brandLight: "#FFF3F6",
    green: "#087A58",
    greenLight: "#EAF8F2",
    red: "#B42335",
    redLight: "#FFF0F1",
  };

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 48;
  const right = pageWidth - margin;
  const contentWidth = right - margin;

  const text = (value, x, y, options = {}) => {
    const {
      size = 10,
      bold = false,
      color = colors.ink,
      align = "left",
      maxWidth,
    } = options;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...hexRgb(color));
    doc.text(String(value), x, y, {
      align,
      ...(maxWidth ? { maxWidth } : {}),
    });
  };

  const fill = (x, y, width, height, color) => {
    doc.setFillColor(...hexRgb(color));
    doc.rect(x, y, width, height, "F");
  };

  const rule = (y, color = colors.line, x1 = margin, x2 = right) => {
    doc.setDrawColor(...hexRgb(color));
    doc.setLineWidth(0.7);
    doc.line(x1, y, x2, y);
  };

  const organization = "Karanki Matrimony";
  const email = "karankimatrimony2020@gmail.com";
  const address = "Hyderabad, Telangana, India";
  const reference = safe(receipt.receiptId);
  const paidColor = isFailed ? colors.red : colors.green;
  const paidBackground = isFailed ? colors.redLight : colors.greenLight;

  fill(0, 0, pageWidth, 8, colors.brand);

  doc.setFillColor(...hexRgb(colors.brandLight));
  doc.roundedRect(margin, 36, 38, 38, 12, 12, "F");
  text("K", margin + 19, 62, {
    size: 20,
    bold: true,
    color: colors.brand,
    align: "center",
  });

  text(organization, margin + 50, 51, {
    size: 16,
    bold: true,
    color: colors.ink,
  });
  text("PREMIUM MEMBERSHIP", margin + 50, 68, {
    size: 8,
    bold: true,
    color: colors.soft,
  });
  text("PAYMENT RECEIPT", right, 48, {
    size: 8,
    bold: true,
    color: colors.muted,
    align: "right",
  });
  text(paidAt, right, 65, {
    size: 9,
    color: colors.ink,
    align: "right",
  });

  rule(94);

  const summaryY = 116;
  doc.setFillColor(...hexRgb(isFailed ? colors.redLight : colors.brandLight));
  doc.roundedRect(margin, summaryY, contentWidth, 92, 14, 14, "F");
  text(isFailed ? "PAYMENT STATUS" : "AMOUNT PAID", margin + 20, summaryY + 24, {
    size: 8,
    bold: true,
    color: paidColor,
  });
  text(safe(receipt.planName || "Karanki Premium"), margin + 20, summaryY + 45, {
    size: 11,
    bold: true,
    color: colors.ink,
    maxWidth: contentWidth - 185,
  });
  text(safe(receipt.durationLabel || PREMIUM_DURATION_LABEL), margin + 20, summaryY + 64, {
    size: 9,
    color: colors.muted,
  });

  text(amount, right - 20, summaryY + 47, {
    size: 22,
    bold: true,
    color: colors.ink,
    align: "right",
  });
  doc.setFillColor(...hexRgb(paidBackground));
  doc.roundedRect(right - 75, summaryY + 59, 55, 20, 10, 10, "F");
  text(isFailed ? "FAILED" : "PAID", right - 47.5, summaryY + 73, {
    size: 8,
    bold: true,
    color: paidColor,
    align: "center",
  });

  text("Transaction details", margin, 242, {
    size: 12,
    bold: true,
    color: colors.ink,
  });
  text(`Receipt ID  ${reference}`, right, 242, {
    size: 8,
    color: colors.muted,
    align: "right",
    maxWidth: contentWidth * 0.52,
  });

  const rows = [
    ["Member", safe(receipt.memberName)],
    ["Plan", safe(receipt.planName || "Karanki Premium")],
    ["Duration", safe(receipt.durationLabel || PREMIUM_DURATION_LABEL)],
    receipt.originalAmount != null && receipt.originalAmount !== receipt.amount
      ? ["Original price", formatAmountPaise(receipt.originalAmount, receipt.currency)]
      : null,
    receipt.discountAmount
      ? [
          receipt.couponCode ? `Coupon (${receipt.couponCode})` : "Discount",
          `−${formatAmountPaise(receipt.discountAmount, receipt.currency)}`,
        ]
      : null,
    receipt.gstAmount != null
      ? [
          `GST${receipt.gstPercent != null ? ` (${receipt.gstPercent}%)` : ""}`,
          formatAmountPaise(receipt.gstAmount, receipt.currency),
        ]
      : null,
    ["Paid on", paidAt],
    ["Valid from", validFrom],
    ["Valid until", validUntil],
    ["Payment ID", reference],
    receipt.orderId ? ["Order ID", safe(receipt.orderId)] : null,
    receipt.paymentMethod
      ? [
          "Payment method",
          receipt.paymentMethodDetail
            ? `${receipt.paymentMethod} · ${receipt.paymentMethodDetail}`
            : receipt.paymentMethod,
        ]
      : null,
  ].filter(Boolean);

  let y = 258;
  const labelX = margin + 14;
  const valueX = right - 14;
  const valueWidth = contentWidth - 170;

  rows.forEach(([label, value], index) => {
    const wrappedValue = doc.splitTextToSize(String(value), valueWidth);
    const rowHeight = Math.max(34, wrappedValue.length * 12 + 18);

    if (index % 2 === 0) {
      fill(margin, y, contentWidth, rowHeight, colors.pale);
    }
    text(label, labelX, y + 21, {
      size: 9,
      color: colors.muted,
    });
    doc.setFont("helvetica", label === "Valid until" ? "bold" : "normal");
    doc.setFontSize(9);
    doc.setTextColor(...hexRgb(
      label === "Valid until"
        ? colors.brand
        : label.startsWith("Coupon") || label === "Discount"
          ? colors.green
          : colors.ink
    ));
    doc.text(wrappedValue, valueX, y + 21, {
      align: "right",
      maxWidth: valueWidth,
    });
    rule(y + rowHeight, colors.line);
    y += rowHeight;
  });

  if (receipt.originalAmount && receipt.amount != null && receipt.originalAmount > receipt.amount) {
    const saved = formatAmountPaise(receipt.originalAmount - receipt.amount, receipt.currency);
    y += 14;
    doc.setFillColor(...hexRgb(colors.greenLight));
    doc.roundedRect(margin, y, contentWidth, 34, 10, 10, "F");
    text(`You saved ${saved} on this purchase`, pageWidth / 2, y + 22, {
      size: 9,
      bold: true,
      color: colors.green,
      align: "center",
    });
    y += 34;
  }

  const footerY = Math.min(Math.max(y + 38, 748), pageHeight - 52);
  rule(footerY - 14);
  text(`Thank you for choosing ${organization}.`, pageWidth / 2, footerY + 2, {
    size: 9,
    bold: true,
    color: colors.muted,
    align: "center",
  });
  text("This is a system-generated receipt and does not require a signature.", pageWidth / 2, footerY + 17, {
    size: 8,
    color: colors.soft,
    align: "center",
  });
  text(`${email}  ·  ${address}`, pageWidth / 2, footerY + 32, {
    size: 7.5,
    color: colors.soft,
    align: "center",
  });

  doc.save(`Karanki-Receipt-${reference.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`);
  return true;
}
